import { ReactNode, useEffect, useRef, useState } from "react";
import classNames from "../../utils/classNames";

export interface IButtonGroupOption {
  id: string;
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
  onClick?: () => void; // For action mode
  isLoading?: boolean; // For action mode
  customContent?: ReactNode; // For custom content like loading spinners
}

export interface IButtonGroupProps {
  options: IButtonGroupOption[];
  selectedIndex?: number | null | undefined; // Optional for action mode
  onChange?: (id: string) => void; // Optional for action mode
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  actionMode?: boolean; // If true, uses onClick from options instead of onChange
  sticky?: boolean;
  stickyTopClassName?: string;
  // Let the group break onto multiple rows instead of overflowing its container.
  wrap?: boolean;
}

export default function ButtonGroup({
  options,
  selectedIndex,
  onChange,
  disabled = false,
  size = "md",
  actionMode = false,
  sticky = false,
  stickyTopClassName = "top-20",
  wrap = false,
}: IButtonGroupProps) {
  const groupRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  const sizeClasses = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };

  useEffect(() => {
    if (!sticky) {
      setIsStuck(false);
      return;
    }

    const updateStuckState = () => {
      const group = groupRef.current;

      if (!group) return;

      const top = parseFloat(window.getComputedStyle(group).top || "0");
      setIsStuck(group.getBoundingClientRect().top <= top + 1);
    };

    updateStuckState();
    window.addEventListener("scroll", updateStuckState, { passive: true });
    window.addEventListener("resize", updateStuckState);

    return () => {
      window.removeEventListener("scroll", updateStuckState);
      window.removeEventListener("resize", updateStuckState);
    };
  }, [sticky]);

  return (
    <div
      ref={groupRef}
      className={classNames(
        wrap ? "flex flex-wrap" : "inline-flex",
        "rounded-md border border-gray-300 overflow-hidden transition-all duration-200",
        sticky ? `sticky ${stickyTopClassName} z-50 -m-1` : undefined,
        sticky && isStuck
          ? "p-2 bg-white shadow-lg ring-1 ring-primary-100 border-primary-200 backdrop-blur rounded-lg"
          : "shadow-sm"
      )}
      role="group"
    >
      {options.map((option, index) => {
        const isActive = !actionMode && selectedIndex !== null && selectedIndex !== undefined && selectedIndex === index;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isDisabled = disabled || option.disabled || option.isLoading;

        // console.log("ButtonGroup Option:", option, "isActive:", isActive, "isDisabled:", isDisabled, "Value:", value);

        const handleClick = () => {
          if (isDisabled) return;
          if (actionMode && option.onClick) {
            option.onClick();
          } else if (!actionMode && onChange) {
            onChange(option.id);
          }
        };

        return (
          <button
            key={index}
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            className={`
              ${sizeClasses[size]}
              font-medium
              transition-colors
              whitespace-nowrap
              max-w-none
              min-w-fit
              border-r border-gray-300
              ${isLast ? "border-r-0" : ""}
              ${wrap ? "flex-1 border-b -mr-px -mb-px" : ""}
              ${isFirst ? "rounded-l-md" : "rounded-none"}
              ${isLast ? "rounded-r-md" : "rounded-none"}
              ${option.isLoading
                ? "bg-primary-600 text-white border-primary-600 opacity-50 cursor-not-allowed"
                : isActive
                  ? "bg-primary-600 text-white border-primary-600 z-10"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
              ${isDisabled && !option.isLoading
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
              }
              focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              ${option.customContent ? "flex items-center justify-center" : ""}
            `}
          >
            {option.customContent || option.label}
          </button>
        );
      })}
    </div>
  );
}
